import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useAction, useMutation, useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import {
  X,
  Upload,
  Image as ImageIcon,
  Film,
  Play,
  Music2,
  Copy,
  Plus,
  Search,
  Check,
  ArrowUpFromLine,
  Sparkles,
  LayoutGrid,
  Trash2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'
import { Lightbox } from '@/components/ui/lightbox'
import type { ChatAttachment } from './WorkflowChat'

type AssetRow = {
  _id: Id<'assets'>
  type: 'image' | 'video' | 'audio'
  source: 'generated' | 'uploaded' | 'extracted'
  crawled?: boolean
  url: string
  createdAt: number
  metadata?: {
    width?: number
    height?: number
    duration?: number
    mimeType?: string
    fileSize?: number
  }
  generator?: {
    model: string
    prompt?: string
  }
  isNewGenerated?: boolean
}

function inferAssetType(file: File): AssetRow['type'] | null {
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('video/')) return 'video'
  if (file.type.startsWith('audio/')) return 'audio'
  return null
}

function getMediaDuration(file: File, kind: 'audio' | 'video'): Promise<number> {
  return new Promise((resolve, reject) => {
    const el = document.createElement(kind)
    el.preload = 'metadata'
    el.onloadedmetadata = () => {
      URL.revokeObjectURL(el.src)
      resolve(Number.isFinite(el.duration) ? el.duration : 0)
    }
    el.onerror = () => {
      URL.revokeObjectURL(el.src)
      reject(new Error(`Failed to load ${kind} metadata for ${file.name}`))
    }
    el.src = URL.createObjectURL(file)
  })
}

async function measureImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  if (!file.type.startsWith('image/')) return null
  return new Promise((resolve) => {
    const el = new window.Image()
    el.onload = () => {
      const dims = { width: el.naturalWidth, height: el.naturalHeight }
      URL.revokeObjectURL(el.src)
      resolve(dims)
    }
    el.onerror = () => {
      URL.revokeObjectURL(el.src)
      resolve(null)
    }
    el.src = URL.createObjectURL(file)
  })
}

const SUPPORTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp'])

function convertImageToPng(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) { URL.revokeObjectURL(img.src); reject(new Error('Canvas not supported')); return }
      ctx.drawImage(img, 0, 0)
      URL.revokeObjectURL(img.src)
      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('Conversion failed')); return }
        const name = file.name.replace(/\.[^.]+$/, '.png')
        resolve(new File([blob], name, { type: 'image/png' }))
      }, 'image/png')
    }
    img.onerror = () => { URL.revokeObjectURL(img.src); reject(new Error(`Cannot decode ${file.name}`)) }
    img.src = URL.createObjectURL(file)
  })
}

function prettyBytes(bytes?: number) {
  if (!bytes || bytes <= 0) return null
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let i = 0
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i++
  }
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

function cleanAssetName(url: string, prompt?: string): string {
  if (prompt) {
    const trimmed = prompt.trim()
    return trimmed.length > 60 ? trimmed.slice(0, 57) + '...' : trimmed
  }
  const filename = url.split('/').pop()?.split('?')[0] ?? 'Asset'
  const cleaned = filename
    .replace(/\.[^.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
  return cleaned.length > 50 ? cleaned.slice(0, 47) + '...' : cleaned
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return `${Math.floor(days / 30)}mo ago`
}

const sourceLabel = (s: AssetRow['source']) =>
  s === 'generated' ? 'Generated' : s === 'uploaded' ? 'Uploaded' : 'Extracted'

type FilterType = 'all' | AssetRow['type']

const FILTERS: { key: FilterType; label: string; icon?: typeof ImageIcon }[] = [
  { key: 'all', label: 'All', icon: LayoutGrid },
  { key: 'image', label: 'Images', icon: ImageIcon },
  { key: 'video', label: 'Videos', icon: Film },
  { key: 'audio', label: 'Audio', icon: Music2 },
]

export function AssetLibrarySheet(props: {
  workflowId: Id<'workflows'>
  open: boolean
  onOpenChange: (open: boolean) => void
  onAddAttachment?: (attachment: ChatAttachment) => void
  onAddToTimeline?: (assetId: Id<'assets'>) => void
  onUploadProgress?: (progress: { current: number; total: number } | null) => void
}) {
  const { workflowId, open, onOpenChange, onAddAttachment, onAddToTimeline, onUploadProgress } = props
  const [filter, setFilter] = useState<FilterType>('all')
  const [query, setQuery] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(
    null
  )
  const [isDragOver, setIsDragOver] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [lightboxType, setLightboxType] = useState<'image' | 'video'>('image')
  const lightboxOpenRef = useRef(false)
  lightboxOpenRef.current = !!lightboxUrl
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const dragCounter = useRef(0)
  const handleUploadFilesRef = useRef<(files: FileList) => Promise<void>>(null!)

  const assets = useQuery(
    api.assets.listForWorkflow,
    open ? { workflowId, limit: 250 } : 'skip'
  ) as AssetRow[] | undefined

  const generateUploadUrl = useMutation(api.workflows.generateUploadUrl)
  const getStorageUrl = useMutation(api.workflows.getStorageUrl)
  const createUploadedAsset = useMutation(api.assets.createUploadedAssetForWorkflow)
  const deleteAssetMutation = useMutation(api.assets.deleteAsset)
  const upscaleImageAction = useAction(api.assets.upscaleImage)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setFilter('all')
    setQuery('')
    setCopiedId(null)
    setConfirmDeleteId(null)
  }, [open])

  useEffect(() => {
    if (!open) return
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items
      if (!items) return
      const files: File[] = []
      for (const item of items) {
        if (item.type.startsWith('image/') || item.type.startsWith('video/') || item.type.startsWith('audio/')) {
          const file = item.getAsFile()
          if (file) files.push(file)
        }
      }
      if (files.length > 0) {
        e.preventDefault()
        const dt = new DataTransfer()
        files.forEach((f) => dt.items.add(f))
        handleUploadFilesRef.current(dt.files)
      }
    }
    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [open])

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [filter])

  const counts = useMemo(() => {
    const rows = assets ?? []
    return {
      all: rows.length,
      image: rows.filter((a) => a.type === 'image').length,
      video: rows.filter((a) => a.type === 'video').length,
      audio: rows.filter((a) => a.type === 'audio').length,
    }
  }, [assets])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const rows = assets ?? []
    return rows.filter((a) => {
      if (filter !== 'all' && a.type !== filter) return false
      if (!q) return true
      const haystack = [
        a.url,
        a.source,
        a.crawled ? 'crawled' : '',
        a.generator?.model ?? '',
        a.generator?.prompt ?? '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [assets, filter, query])

  const handleUploadFiles = async (files: FileList) => {
    const list = Array.from(files)
    if (list.length === 0) return

    const maxImage = 25 * 1024 * 1024
    const maxAudio = 50 * 1024 * 1024
    const maxVideo = 200 * 1024 * 1024

    setIsUploading(true)
    setUploadProgress({ current: 0, total: list.length })
    onUploadProgress?.({ current: 0, total: list.length })
    onOpenChange(false)
    try {
      for (let i = 0; i < list.length; i++) {
        let file = list[i]
        setUploadProgress({ current: i + 1, total: list.length })
        onUploadProgress?.({ current: i + 1, total: list.length })

        let type = inferAssetType(file)
        if (!type) {
          toast({ title: 'Unsupported file', description: `Skipping ${file.name}` })
          continue
        }

        // Convert unsupported image formats (AVIF, HEIC, BMP, etc.) to PNG
        if (type === 'image' && !SUPPORTED_IMAGE_TYPES.has(file.type)) {
          try {
            file = await convertImageToPng(file)
          } catch {
            toast({ title: 'Unsupported image format', description: `Could not convert ${file.name}` })
            continue
          }
        }

        const maxSize = type === 'image' ? maxImage : type === 'audio' ? maxAudio : maxVideo
        if (file.size > maxSize) {
          toast({
            title: 'File too large',
            description: `${file.name} is ${prettyBytes(file.size)} (max ${prettyBytes(maxSize)})`,
          })
          continue
        }

        let duration: number | undefined
        if (type === 'audio') {
          duration = await getMediaDuration(file, 'audio')
        } else if (type === 'video') {
          duration = await getMediaDuration(file, 'video')
        }

        let imageDims: { width: number; height: number } | null = null
        if (type === 'image') {
          imageDims = await measureImageDimensions(file)
        }

        const uploadUrl = await generateUploadUrl()
        const res = await fetch(uploadUrl, {
          method: 'POST',
          body: file,
          headers: { 'Content-Type': file.type },
        })
        if (!res.ok) {
          const text = await res.text().catch(() => '')
          throw new Error(`Upload failed for ${file.name}${text ? `: ${text}` : ''}`)
        }

        const { storageId } = await res.json()
        let url = await getStorageUrl({ storageId })
        if (!url) throw new Error(`Missing storage URL for ${file.name}`)

        if (imageDims) {
          const upscaled = await upscaleImageAction({
            imageUrl: url,
            width: imageDims.width,
            height: imageDims.height,
          })
          if (upscaled) {
            url = upscaled.url
            imageDims = { width: upscaled.width, height: upscaled.height }
          }
        }

        await createUploadedAsset({
          workflowId,
          type,
          url,
          metadata: {
            duration,
            mimeType: file.type,
            fileSize: file.size,
            ...(imageDims && { width: imageDims.width, height: imageDims.height }),
          },
        })

        if (onAddAttachment) {
          if (type === 'image') {
            onAddAttachment({ type: 'image', url, storageId, name: file.name })
          } else if (type === 'audio' && duration !== undefined) {
            onAddAttachment({
              type: 'audio',
              url,
              storageId,
              name: file.name,
              duration,
              mimeType: file.type,
            })
          }
        }
      }
    } catch (e) {
      toast({
        title: 'Upload failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      })
    } finally {
      setIsUploading(false)
      setUploadProgress(null)
      onUploadProgress?.(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }
  handleUploadFilesRef.current = handleUploadFiles

  const copyUrl = useCallback(async (id: string, url: string) => {
    const fallbackCopy = (text: string) => {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.cssText = 'position:fixed;opacity:0;left:-9999px'
      document.body.appendChild(ta)
      ta.focus()
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      try {
        fallbackCopy(url)
      } catch {
        toast({ title: 'Copy failed', description: 'Could not copy to clipboard' })
        return
      }
    }
    setCopiedId(id)
    setTimeout(() => setCopiedId((prev) => (prev === id ? null : prev)), 1500)
  }, [])

  const addFromAsset = useCallback(
    (asset: AssetRow) => {
      if ((asset.type === 'video' || asset.type === 'image') && onAddToTimeline) {
        onAddToTimeline(asset._id)
        onOpenChange(false)
        return
      }
      if (!onAddAttachment) return
      if (asset.type === 'image') {
        onAddAttachment({ type: 'image', url: asset.url, name: asset.url.split('/').pop() })
        onOpenChange(false)
        return
      }
      if (asset.type === 'audio') {
        const duration = asset.metadata?.duration
        const mimeType = asset.metadata?.mimeType
        if (!duration || !mimeType) {
          toast({
            title: 'Missing metadata',
            description: 'This audio asset cannot be attached yet.',
          })
          return
        }
        onAddAttachment({
          type: 'audio',
          url: asset.url,
          name: asset.url.split('/').pop(),
          duration,
          mimeType,
        })
        onOpenChange(false)
      }
    },
    [onAddAttachment, onAddToTimeline, onOpenChange]
  )

  const handleDelete = useCallback(
    async (assetId: Id<'assets'>) => {
      if (confirmDeleteId !== assetId) {
        setConfirmDeleteId(assetId)
        return
      }
      try {
        await deleteAssetMutation({ assetId })
        setConfirmDeleteId(null)
      } catch {
        toast({ title: 'Delete failed', description: 'Could not delete this asset.' })
      }
    },
    [confirmDeleteId, deleteAssetMutation]
  )

  const isLoading = open && assets === undefined

  return (
    <>
    <DialogPrimitive.Root open={open} onOpenChange={(v) => {
      if (!v && lightboxOpenRef.current) return
      onOpenChange(v)
    }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[3px] data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out" />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-50 left-0 right-0 bottom-0',
            'max-h-[85vh] w-full flex flex-col',
            'rounded-t-2xl border-t border-border bg-secondary shadow-[0_-20px_60px_-20px_rgba(0,0,0,0.4)]',
            'focus:outline-none',
            'translate-y-full',
            'data-[state=open]:translate-y-0',
            'data-[state=open]:animate-sheet-up data-[state=closed]:animate-sheet-down'
          )}
          onInteractOutside={(e) => {
            if (lightboxOpenRef.current) e.preventDefault()
          }}
          onEscapeKeyDown={(e) => {
            if (lightboxOpenRef.current) e.preventDefault()
          }}
          onDragEnter={(e) => {
            e.preventDefault()
            dragCounter.current++
            setIsDragOver(true)
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => {
            dragCounter.current--
            if (dragCounter.current === 0) setIsDragOver(false)
          }}
          onDrop={(e) => {
            e.preventDefault()
            dragCounter.current = 0
            setIsDragOver(false)
            if (e.dataTransfer.files.length > 0) handleUploadFiles(e.dataTransfer.files)
          }}
        >
          {isDragOver && (
            <div className="absolute inset-0 z-20 bg-background/95 backdrop-blur-sm rounded-t-2xl flex items-center justify-center border-2 border-dashed border-border">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-foreground text-background flex items-center justify-center">
                  <ArrowUpFromLine size={22} />
                </div>
                <p className="text-sm font-medium text-foreground">Drop files to upload</p>
                <p className="text-xs text-muted-foreground">Images, videos, and audio</p>
              </div>
            </div>
          )}

          {/* Header — minimal */}
          <div className="shrink-0 px-4 pt-3 pb-2 bg-card rounded-t-2xl">
            <div className="flex justify-center mb-2">
              <div className="h-1 w-8 rounded-full bg-border" />
            </div>
            <div className="flex items-center justify-between">
              <DialogPrimitive.Title className="text-[15px] font-semibold text-foreground tracking-tight">
                Asset Library
                {counts.all > 0 && (
                  <span className="ml-1.5 text-[11px] font-normal tabular-nums text-muted-foreground">
                    {counts.all}
                  </span>
                )}
              </DialogPrimitive.Title>
              <DialogPrimitive.Close
                className="shrink-0 w-7 h-7 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 hover:text-secondary-foreground transition-colors flex items-center justify-center"
                aria-label="Close"
              >
                <X size={14} />
              </DialogPrimitive.Close>
            </div>
          </div>

          {/* Content */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain"
          >
            {isLoading ? (
              <div className="p-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-1.5">
                {Array.from({ length: 18 }).map((_, i) => (
                  <div key={i} className="rounded-lg overflow-hidden bg-card border border-border">
                    <div className="aspect-4/3 bg-muted animate-pulse" />
                    <div className="px-2 py-1.5 space-y-1">
                      <div className="h-3 w-3/4 bg-muted rounded animate-pulse" />
                      <div className="h-2.5 w-1/2 bg-muted rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-14 text-center">
                {!query && counts.all === 0 ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="mx-auto w-full max-w-sm px-6 py-10 rounded-2xl border-2 border-dashed border-border hover:border-border/80 bg-card hover:bg-accent transition-all cursor-pointer flex flex-col items-center justify-center gap-3 group"
                  >
                    <div className="w-11 h-11 rounded-xl bg-foreground text-background flex items-center justify-center group-hover:scale-105 transition-transform">
                      <ArrowUpFromLine size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-secondary-foreground">
                        Upload images, videos, or audio
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Drag and drop or click to browse
                      </p>
                    </div>
                  </button>
                ) : (
                  <>
                    <div className="mx-auto w-10 h-10 rounded-xl bg-muted text-muted-foreground flex items-center justify-center">
                      <Search size={16} />
                    </div>
                    <p className="mt-2.5 text-sm font-medium text-secondary-foreground">No results</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Try a different search or filter
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="p-2.5">
                {query && (
                  <p className="text-[11px] text-muted-foreground mb-2 px-0.5">
                    {filtered.length} result{filtered.length !== 1 ? 's' : ''}
                  </p>
                )}
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-1.5">
                  {filtered.map((asset) => (
                    <AssetCard
                      key={asset._id}
                      asset={asset}
                      canAttach={
                        (!!onAddToTimeline && (asset.type === 'video' || asset.type === 'image')) ||
                        (!!onAddAttachment && (asset.type === 'image' || asset.type === 'audio'))
                      }
                      isCopied={copiedId === asset._id}
                      isConfirmingDelete={confirmDeleteId === asset._id}
                      onAdd={() => addFromAsset(asset)}
                      onCopy={() => copyUrl(asset._id, asset.url)}
                      onDelete={() => handleDelete(asset._id)}
                      onCancelDelete={() => setConfirmDeleteId(null)}
                      onView={() => {
                        setLightboxUrl(asset.url)
                        setLightboxType(asset.type === 'video' ? 'video' : 'image')
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Bottom toolbar */}
          <div className="shrink-0 px-6 py-3.5 bg-card border-t border-border flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className={cn(
                'h-8 px-3.5 rounded-lg text-xs font-medium transition-all inline-flex items-center gap-2 whitespace-nowrap',
                isUploading
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.97] shadow-sm'
              )}
            >
              <Upload size={13} />
              {uploadProgress
                ? `Uploading ${uploadProgress.current}/${uploadProgress.total}`
                : isUploading
                  ? 'Uploading...'
                  : 'Upload files'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*,audio/*"
              multiple
              className="hidden"
              onChange={(e) => e.target.files && handleUploadFiles(e.target.files)}
            />

            <span className="text-[10px] text-muted-foreground">or paste / drag</span>

            <div className="flex-1 min-w-[200px] ml-8 relative">
              <Search
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape' && query) {
                    e.preventDefault()
                    e.stopPropagation()
                    setQuery('')
                  }
                }}
                placeholder="Search assets..."
                className="w-full h-8 rounded-lg border border-border bg-background pl-7 pr-7 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring transition-shadow"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 flex items-center justify-center"
                >
                  <X size={8} />
                </button>
              )}
            </div>

            <div className="flex gap-0.5 p-0.5 ml-8 rounded-lg bg-muted/80">
              {FILTERS.map(({ key, label, icon: Icon }) => {
                const count = counts[key]
                const active = filter === key
                const empty = key !== 'all' && count === 0
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilter(key)}
                    disabled={empty}
                    className={cn(
                      'h-7 px-2 rounded-md text-[11px] font-medium transition-all duration-150 inline-flex items-center gap-1',
                      active
                        ? 'bg-card text-foreground shadow-sm'
                        : empty
                          ? 'text-muted-foreground/50 cursor-default'
                          : 'text-muted-foreground hover:text-secondary-foreground'
                    )}
                  >
                    {Icon && <Icon size={11} />}
                    {label}
                    {count > 0 && key !== 'all' && (
                      <span
                        className={cn(
                          'text-[10px] tabular-nums',
                          active ? 'text-muted-foreground' : 'text-muted-foreground'
                        )}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
    <Lightbox url={lightboxUrl} type={lightboxType} onClose={() => setLightboxUrl(null)} />
    </>
  )
}

function AssetCard(props: {
  asset: AssetRow
  canAttach: boolean
  isCopied: boolean
  isConfirmingDelete: boolean
  onAdd: () => void
  onCopy: () => void
  onDelete: () => void
  onCancelDelete: () => void
  onView: () => void
}) {
  const { asset, canAttach, isCopied, isConfirmingDelete, onAdd, onCopy, onDelete, onCancelDelete, onView } = props
  const isImage = asset.type === 'image'
  const isVideo = asset.type === 'video'
  const isAudio = asset.type === 'audio'
  const name = cleanAssetName(asset.url, asset.generator?.prompt)

  const hasDuration = (isVideo || isAudio) && typeof asset.metadata?.duration === 'number'

  const durationBadge = hasDuration && (
    <div className="absolute bottom-1 right-1 pointer-events-none">
      <span
        className={cn(
          'text-[9px] font-medium tabular-nums px-1.5 py-px rounded leading-normal backdrop-blur-sm',
          isVideo ? 'bg-black/50 text-white/90' : 'bg-amber-900/40 text-amber-100'
        )}
      >
        {formatDuration(asset.metadata!.duration!)}
      </span>
    </div>
  )

  const thumbnailContent = isImage ? (
    <div className="relative h-full w-full">
      <div className="absolute inset-0 flex items-center justify-center">
        <ImageIcon size={16} className="text-muted-foreground/50" />
      </div>
      <img
        src={asset.url}
        alt={name}
        loading="lazy"
        className="relative h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        onError={(e) => {
          e.currentTarget.style.display = 'none'
        }}
      />
    </div>
  ) : isVideo ? (
    <div className="relative h-full w-full">
      <div className="absolute inset-0 flex items-center justify-center bg-neutral-800">
        <Film size={16} className="text-neutral-500" />
      </div>
      <video
        src={asset.url}
        preload="metadata"
        muted
        playsInline
        className="relative h-full w-full object-cover"
        onError={(e) => {
          e.currentTarget.style.display = 'none'
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
          <Play size={14} className="text-white fill-white ml-px" />
        </div>
      </div>
      {durationBadge}
    </div>
  ) : (
    <div
      className={cn(
        'h-full w-full flex flex-col items-center justify-center gap-1.5',
        'bg-linear-to-br from-amber-50 to-orange-100'
      )}
    >
      <Music2 size={20} className="text-amber-600/60" />
      {durationBadge}
    </div>
  )

  const meta: string[] = [sourceLabel(asset.source)]
  if (asset.createdAt) meta.push(relativeTime(asset.createdAt))
  if (asset.generator?.model) meta.push(asset.generator.model)

  return (
    <div
      className={cn(
        'group relative rounded-lg overflow-hidden',
        'bg-card border transition-all duration-150',
        'hover:shadow-md hover:border-border',
        'focus-within:shadow-md focus-within:border-border',
        asset.isNewGenerated
          ? 'border-primary/40 ring-1 ring-primary/20'
          : 'border-border/70'
      )}
      style={{ contentVisibility: 'auto', containIntrinsicSize: '0 160px' }}
    >
      {/* Thumbnail — click opens lightbox */}
      <div
        role="button"
        tabIndex={0}
        onClick={onView}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onView() } }}
        className="block w-full aspect-4/3 bg-muted relative overflow-hidden cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        {thumbnailContent}
        {canAttach && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onAdd()
            }}
            className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-primary shadow-lg shadow-primary/30 flex items-center justify-center opacity-0 scale-75 pointer-events-none group-hover:opacity-100 group-hover:scale-100 group-hover:pointer-events-auto hover:bg-primary/90 transition-all duration-200 z-10"
            aria-label="Attach to chat"
          >
            <Plus size={18} strokeWidth={2.5} className="text-white" />
          </button>
        )}
      </div>

      {/* Thumbnail badges */}
      <div className="absolute top-1 left-1 flex gap-0.5 pointer-events-none">
        {asset.isNewGenerated && (
          <span className="text-[8px] font-bold tracking-wider uppercase px-1.5 py-px rounded bg-primary/90 text-white leading-normal backdrop-blur-sm">
            New
          </span>
        )}
        {asset.crawled && (
          <span className="text-[8px] font-medium px-1.5 py-px rounded bg-black/40 text-white/90 leading-normal backdrop-blur-sm">
            Crawled
          </span>
        )}
      </div>

      {/* Info row */}
      <div className="px-1.5 py-1 flex items-start gap-1">
        {isConfirmingDelete ? (
          <div className="flex items-center gap-1 w-full">
            <p className="text-[10px] text-destructive font-medium flex-1 truncate">Delete?</p>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete() }}
              className="shrink-0 h-5 px-1.5 rounded text-[10px] font-medium bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
            >
              Yes
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onCancelDelete() }}
              className="shrink-0 h-5 px-1.5 rounded text-[10px] font-medium bg-muted text-secondary-foreground hover:bg-muted/80 transition-colors"
            >
              No
            </button>
          </div>
        ) : (
          <>
            <div className="min-w-0 flex-1">
              <p
                className="text-[10px] font-medium text-secondary-foreground truncate leading-tight"
                title={name}
              >
                {asset.source === 'generated' && (
                  <Sparkles size={9} className="inline-block mr-0.5 -mt-px text-amber-500/70" />
                )}
                {name}
              </p>
              <p className="text-[9px] text-muted-foreground truncate mt-px leading-tight">
                {meta.join(' \u00b7 ')}
              </p>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete() }}
              className="shrink-0 w-5 h-5 rounded flex items-center justify-center transition-colors mt-px text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100"
              aria-label="Delete asset"
              tabIndex={0}
            >
              <Trash2 size={9} />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onCopy()
              }}
              className={cn(
                'shrink-0 w-5 h-5 rounded flex items-center justify-center transition-colors mt-px',
                isCopied
                  ? 'bg-success/10 text-success'
                  : 'text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted'
              )}
              aria-label="Copy URL"
              tabIndex={0}
            >
              {isCopied ? <Check size={9} /> : <Copy size={9} />}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
